import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import FilterOptions from './components/FilterOptions';
import { getApiErrorMessage } from '../../api';
import { fuelingHistoryService } from '../../api/fueling.history';
import { maintenanceService } from '../../api/maintenance';
import { tripService } from '../../api/trips';
import { vehicleService, type Vehicle } from '../../api/vehicles';
import {
  Car,
  Search,
  RefreshCw,
  Filter,
  Gauge,
  Wrench,
  CheckCircle2,
  Clock,
  Fuel,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
} from 'lucide-react';
import {
  PageHeader,
  StatCard,
  Button,
  Badge,
  Input,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '../../components/ui/SharedLayout';

type VehicleOverview = Vehicle & {
  activeTrips: number;
  completedTrips: number;
  activeMaintenances: number;
  totalFuelCost: number;
  latestFuelingAt: string | null;
  assignedEmployeeName: string | null;
};

const ITEMS_PER_PAGE = 20;

const CadastroVeiculos = () => {
  const { setPageTitle } = useAuth();
  const [vehicles, setVehicles] = useState<VehicleOverview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string[]>>({});
  const [filterModalKey, setFilterModalKey] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setPageTitle('Frota do setor');
  }, [setPageTitle]);

  const filterCategories = {
    Status: [
      { id: 'disponivel', label: 'Disponível' },
      { id: 'manutencao', label: 'Em Manutenção' },
      { id: 'em_uso', label: 'Em Uso' },
    ],
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [scopedVehicles, scopedTrips, fuelingHistory, activeMaintenances] =
        await Promise.all([
          vehicleService.getAllVehicles(),
          tripService.getAll(),
          fuelingHistoryService.getAllFuelingHistory(),
          maintenanceService.getAll('active'),
        ]);

      const tripStats = new Map<
        number,
        {
          activeTrips: number;
          completedTrips: number;
          assignedEmployeeName: string | null;
        }
      >();

      scopedTrips.forEach((trip) => {
        const current = tripStats.get(trip.vehicle_id) ?? {
          activeTrips: 0,
          completedTrips: 0,
          assignedEmployeeName: null,
        };

        if (trip.status === 'aprovada' || trip.status === 'em_andamento') {
          current.activeTrips += 1;
        }

        if (trip.status === 'concluida') {
          current.completedTrips += 1;
        }

        current.assignedEmployeeName =
          trip.employee?.name || current.assignedEmployeeName;
        tripStats.set(trip.vehicle_id, current);
      });

      const fuelingStats = new Map<
        number,
        { totalFuelCost: number; latestFuelingAt: string | null }
      >();

      fuelingHistory.forEach((item) => {
        const current = fuelingStats.get(item.vehicle_id) ?? {
          totalFuelCost: 0,
          latestFuelingAt: null,
        };

        current.totalFuelCost += Number(item.total_cost) || 0;

        const currentTimestamp = current.latestFuelingAt
          ? new Date(current.latestFuelingAt).getTime()
          : 0;
        const nextTimestamp = new Date(
          item.fueling_time || item.created_at,
        ).getTime();

        if (!current.latestFuelingAt || nextTimestamp > currentTimestamp) {
          current.latestFuelingAt = item.fueling_time || item.created_at;
        }

        fuelingStats.set(item.vehicle_id, current);
      });

      const maintenanceStats = new Map<number, number>();
      activeMaintenances.forEach((item) => {
        maintenanceStats.set(
          item.vehicle_id,
          (maintenanceStats.get(item.vehicle_id) ?? 0) + 1,
        );
      });

      setVehicles(
        scopedVehicles.map((vehicle) => {
          const tripInfo = tripStats.get(vehicle.id);
          const fuelingInfo = fuelingStats.get(vehicle.id);

          return {
            ...vehicle,
            activeTrips: tripInfo?.activeTrips ?? 0,
            completedTrips: tripInfo?.completedTrips ?? 0,
            activeMaintenances: maintenanceStats.get(vehicle.id) ?? 0,
            totalFuelCost: fuelingInfo?.totalFuelCost ?? 0,
            latestFuelingAt: fuelingInfo?.latestFuelingAt ?? null,
            assignedEmployeeName:
              tripInfo?.assignedEmployeeName || null,
          };
        }),
      );
    } catch (loadError) {
      setVehicles([]);
      setError(
        getApiErrorMessage(
          loadError,
          'Erro ao carregar a frota do setor.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const filteredVehicles = useMemo(() => {
    let filtered = [...vehicles];

    const term = searchTerm.trim().toLowerCase();
    if (term) {
      filtered = filtered.filter((vehicle) =>
        [
          vehicle.brand,
          vehicle.description,
          vehicle.license_plate,
          vehicle.department_name,
          vehicle.assignedEmployeeName || '',
        ]
          .join(' ')
          .toLowerCase()
          .includes(term),
      );
    }

    if (Object.keys(appliedFilters).length > 0) {
      Object.entries(appliedFilters).forEach(([category, values]) => {
        if (values.length > 0) {
          filtered = filtered.filter((vehicle) => {
            if (category === 'Status') {
              const isInMaintenance = vehicle.activeMaintenances > 0 || vehicle.status === 'manutencao';
              const normalizado = isInMaintenance
                ? 'manutencao'
                : vehicle.status === 'em_uso'
                  ? 'em_uso'
                  : 'disponivel';
              return values.includes(normalizado);
            }
            return true;
          });
        }
      });
    }

    return filtered;
  }, [searchTerm, appliedFilters, vehicles]);

  const totalPages = Math.max(1, Math.ceil(filteredVehicles.length / ITEMS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [filteredVehicles.length, currentPage, totalPages]);

  const { paginatedVehicles, pageRange } = useMemo(() => {
    const total = filteredVehicles.length;
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const sliced = filteredVehicles.slice(startIndex, endIndex);
    return {
      paginatedVehicles: sliced,
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredVehicles, currentPage, totalPages]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPages - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    pages.push(totalPages);
    return pages;
  }, [currentPage, totalPages]);

  const totals = useMemo(() => {
    const total = vehicles.length;
    const disponiveis = vehicles.filter((v) => {
      const isInMaintenance = v.activeMaintenances > 0 || v.status === 'manutencao';
      return !isInMaintenance && v.status !== 'em_uso';
    }).length;
    const emUso = vehicles.filter((v) => v.status === 'em_uso').length;
    const manutencao = vehicles.filter((v) => v.activeMaintenances > 0 || v.status === 'manutencao').length;
    return { total, disponiveis, emUso, manutencao };
  }, [vehicles]);

  const handleApplyFilter = (selectedFilters: Record<string, string[]>) => {
    setAppliedFilters(selectedFilters);
  };

  const handleOpenFilter = () => {
    setFilterModalKey((k) => k + 1);
    setIsFilterOpen(true);
  };

  const handleClearFilters = () => {
    setAppliedFilters({});
  };

  const appliedFiltersCount = useMemo(() => {
    return Object.values(appliedFilters).reduce(
      (total, values) => total + values.length,
      0,
    );
  }, [appliedFilters]);

  const renderStatusBadge = (vehicle: VehicleOverview) => {
    const isInMaintenance = vehicle.activeMaintenances > 0 || vehicle.status === 'manutencao';
    if (isInMaintenance) {
      return (
        <Badge tone="amber">
          Em manutenção
        </Badge>
      );
    }
    if (vehicle.status === 'em_uso') {
      return (
        <Badge tone="blue">
          Em uso
        </Badge>
      );
    }
    return (
      <Badge tone="emerald">
        Disponível
      </Badge>
    );
  };

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <PageHeader title="Frota do setor" hideTitleInBody />

        <div className="mb-7 flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1.5 max-w-2xl">
            <p className="text-[15px] font-semibold leading-tight text-sky-600">
              Gestão de veículos
            </p>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <h1 className="text-[22px] sm:text-[26px] font-bold tracking-tight leading-tight text-slate-900">
                Acompanhe a frota
              </h1>
              <span className="text-[18px] sm:text-[20px] font-medium text-slate-500 leading-tight">
                do seu setor em tempo real
              </span>
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2 w-full sm:w-auto mt-1 sm:mt-0">
            {appliedFiltersCount > 0 && (
              <Button variant="secondary" onClick={handleClearFilters} className="h-10 px-4">
                <X className="h-4 w-4 stroke-[1.8]" />
                Limpar filtros
              </Button>
            )}
            <Button
              variant={appliedFiltersCount > 0 ? 'primary' : 'secondary'}
              onClick={handleOpenFilter}
              className="h-10 px-4"
            >
              <Filter className="h-4 w-4 stroke-[1.8]" />
              Filtros
              {appliedFiltersCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 rounded-full bg-white/25 text-white text-xs font-semibold px-1.5 ring-1 ring-inset ring-white/30 ml-1">
                  {appliedFiltersCount}
                </span>
              )}
            </Button>
            <Button variant="secondary" onClick={() => void loadData()} className="h-10 px-4">
              <RefreshCw className="h-4 w-4 stroke-[1.8]" />
              Atualizar
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4 mb-7">
          <StatCard
            icon={<Car className="h-5 w-5 min-h-[20px] min-w-[20px] shrink-0 stroke-[1.8]" />}
            label="Total de Veículos"
            value={totals.total}
            trend={<span>Cadastrados no sistema</span>}
            variant="deep"
          />
          <StatCard
            icon={<CheckCircle2 className="h-5 w-5 min-h-[20px] min-w-[20px] shrink-0 stroke-[1.8]" />}
            label="Disponíveis"
            value={totals.disponiveis}
            trend={<span>Prontos para uso</span>}
            variant="primary"
          />
          <StatCard
            icon={<Clock className="h-5 w-5 min-h-[20px] min-w-[20px] shrink-0 stroke-[1.8]" />}
            label="Em Uso"
            value={totals.emUso}
            trend={<span>Em viagem ou operação</span>}
            variant="sky"
          />
          <StatCard
            icon={<Wrench className="h-5 w-5 min-h-[20px] min-w-[20px] shrink-0 stroke-[1.8]" />}
            label="Em Manutenção"
            value={totals.manutencao}
            trend={<span>Oficina / reparos</span>}
            variant="rich"
          />
        </div>

        <SectionCard>
          <CardHeader>
            <div>
              <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                Veículos
              </h2>
              <p className="text-[13px] text-slate-500 mt-0.5">
                {loading
                  ? 'Carregando registros...'
                  : pageRange.total === 0
                    ? 'Nenhum veículo encontrado'
                    : `${pageRange.start} a ${pageRange.end} de ${pageRange.total} veículos listados (total cadastrado: ${vehicles.length})`}
              </p>
            </div>
            <div className="w-full sm:w-[360px] shrink-0 relative">
              <Search
                className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 stroke-[1.8] text-slate-400"
                aria-hidden="true"
              />
              <Input
                type="text"
                name="search-vehicles"
                placeholder="Pesquisar placa, modelo, marca, setor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="!pl-10"
              />
            </div>
          </CardHeader>

          <CardBody className="!py-3">
            {error && (
              <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
                {error}
              </div>
            )}

            {loading ? (
              <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-sm font-medium text-slate-600">
                  Carregando veículos...
                </span>
              </div>
            ) : (
              <Fragment>
                <ModernTable>
                  <Thead>
                    <Tr>
                      <Th>Veículo</Th>
                      <Th>Departamento</Th>
                      <Th>Ano</Th>
                      <Th>Combustível</Th>
                      <Th>Status</Th>
                      <Th>Quilometragem</Th>
                      <Th>Viagens Concluídas</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {pageRange.total === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-5 py-14 text-center text-sm text-slate-500"
                        >
                          Nenhum veículo encontrado
                        </td>
                      </tr>
                    ) : (
                      paginatedVehicles.map((vehicle) => (
                        <Tr key={vehicle.id}>
                          <Td>
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 min-w-[40px] shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 text-white shadow-[0_6px_16px_-8px_rgba(37,99,235,0.6)]">
                                <Car className="h-[18px] w-[18px] min-h-[18px] min-w-[18px] stroke-[1.9]" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="truncate font-semibold text-slate-900">
                                  {[vehicle.brand, vehicle.description].filter(Boolean).join(' ') || 'Veículo sem descrição'}
                                </div>
                                <div className="mt-0.5 font-mono text-[12px] tracking-wide text-slate-500">
                                  {vehicle.license_plate || '—'}
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <span className="font-medium text-slate-700">
                              {vehicle.department_name || '-'}
                            </span>
                          </Td>
                          <Td>
                            <span className="inline-flex items-center rounded-md bg-slate-50 px-2.5 py-1 text-[12px] font-bold text-slate-700 ring-1 ring-inset ring-slate-200 tabular-nums">
                              {vehicle.year || '-'}
                            </span>
                          </Td>
                          <Td>
                            <span className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[12px] font-bold ring-1 ring-inset ${
                              !vehicle.fuel
                                ? 'bg-slate-50 text-slate-400 ring-slate-200'
                                : String(vehicle.fuel).toLowerCase().includes('diesel')
                                  ? 'bg-orange-50 text-orange-700 ring-orange-200'
                                  : String(vehicle.fuel).toLowerCase().includes('flex') || String(vehicle.fuel).toLowerCase().includes('etanol') || String(vehicle.fuel).toLowerCase().includes('gasolina')
                                    ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                                    : String(vehicle.fuel).toLowerCase().includes('gnv')
                                      ? 'bg-sky-50 text-sky-700 ring-sky-200'
                                      : String(vehicle.fuel).toLowerCase().includes('eletrico')
                                        ? 'bg-violet-50 text-violet-700 ring-violet-200'
                                        : 'bg-slate-50 text-slate-700 ring-slate-200'
                            }`}>
                              <Fuel className="h-3 w-3 stroke-[1.9]" />
                              {vehicle.fuel || '—'}
                            </span>
                          </Td>
                          <Td>
                            {renderStatusBadge(vehicle)}
                          </Td>
                          <Td>
                            <div className="inline-flex items-center gap-1.5 text-slate-700">
                              <Gauge className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                              <span className="font-semibold tabular-nums">
                                {vehicle.mileage?.toLocaleString('pt-BR') || 0}
                              </span>
                              <span className="text-slate-400 text-[12px]">km</span>
                            </div>
                          </Td>
                          <Td>
                            <div className="inline-flex items-center gap-1">
                              <CheckCircle2 className="h-3.5 w-3.5 stroke-[1.8] text-emerald-500" />
                              <span className="inline-flex items-center rounded-md bg-emerald-50 px-2.5 py-1 text-[12px] font-bold text-emerald-700 ring-1 ring-inset ring-emerald-200 tabular-nums">
                                {vehicle.completedTrips} concluídas
                              </span>
                            </div>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </ModernTable>

                {!loading && (
                  <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-[13px] font-medium text-slate-500">
                      Mostrando <span className="font-semibold text-slate-700">{pageRange.start}-{pageRange.end}</span> de <span className="font-semibold text-slate-700">{pageRange.total}</span> resultados
                    </div>
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        aria-label="Primeira página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronsLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        aria-label="Página anterior"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <div className="inline-flex items-center gap-1 mx-1">
                        {getPageNumbers.map((n, idx) =>
                          n === '...' ? (
                            <span
                              key={`dots-${idx}`}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]"
                            >
                              …
                            </span>
                          ) : (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setCurrentPage(n)}
                              aria-label={`Ir para página ${n}`}
                              className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 ${
                                n === currentPage
                                  ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]'
                                  : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                              }`}
                            >
                              {n}
                            </button>
                          ),
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        aria-label="Próxima página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronRight className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        aria-label="Última página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronsRight className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                    </div>
                  </div>
                )}
              </Fragment>
            )}
          </CardBody>
        </SectionCard>

        {isFilterOpen && (
          <FilterOptions
            key={filterModalKey}
            isOpen={isFilterOpen}
            onClose={() => setIsFilterOpen(false)}
            onApplyFilter={handleApplyFilter}
            filterCategories={filterCategories}
            initialFilters={appliedFilters}
          />
        )}
      </div>
    </div>
  );
};

export default CadastroVeiculos;
